import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { action, computed, makeAutoObservable, observable } from "mobx";

import { ColumnSelectionStore } from "./column-selection-store";
import { SidebarStore } from "./sidebar-store";

import { BlockType } from "@/components/PowerBlock/Blocks";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isEmptyRecord = (value: unknown): value is Record<string, never> =>
  isRecord(value) && Object.keys(value).length === 0;

const normalizeJsonLikeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeJsonLikeValue(item));
  }

  if (!isRecord(value)) {
    return value;
  }

  const normalized: Record<string, unknown> = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    if (nestedValue === undefined) {
      continue;
    }
    normalized[key] = normalizeJsonLikeValue(nestedValue);
  }

  return normalized;
};

export const normalizeColumnConfiguration = (
  blockType: BlockType,
  config: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  const normalized = normalizeJsonLikeValue(cloneDeep(config ?? {}));
  const normalizedConfig = isRecord(normalized) ? normalized : {};

  delete normalizedConfig.block_type;

  if (blockType === BlockType.PROMPT_TEMPLATE) {
    if (isEmptyRecord(normalizedConfig.prompt_template_variable_mappings)) {
      delete normalizedConfig.prompt_template_variable_mappings;
    }

    if (normalizedConfig.verbose === false) {
      delete normalizedConfig.verbose;
    }

    if (normalizedConfig.return_template_only === false) {
      delete normalizedConfig.return_template_only;
    }

    if (normalizedConfig.hydrate_input_variables === true) {
      delete normalizedConfig.hydrate_input_variables;
    }
  }

  return normalizedConfig;
};

export const normalizeColumnDraftSnapshot = (
  snapshot: ColumnDraftSnapshot,
): ColumnDraftSnapshot => ({
  ...snapshot,
  configuration: normalizeColumnConfiguration(snapshot.blockType, snapshot.configuration),
});

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface ColumnDraftSnapshot {
  columnId: string;
  name: string;
  blockType: BlockType;
  configuration: Record<string, unknown>;
}

export interface ColumnConfig {
  /** The field key from the SheetColumn definition. */
  field: string;
  /**
   * Display name for this column.
   *
   * This is the **committed** name — it only updates when the user
   * explicitly saves via the PowerBlock save button.  Live editing
   * state lives inside BlockStore.name (managed by PowerBlock).
   */
  name: string;
  /** Which PowerBlock type is assigned to this column. */
  blockType: BlockType;
  /** Block-specific configuration blob. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration: any;
}

/** Options for creating a new column config entry. */
export interface CreateColumnConfigOptions {
  /** Display name override. Falls back to the field key. */
  displayName?: string;
  /** Initial block type. Falls back to `defaultBlockType` on the store. */
  blockType?: BlockType;
  /** Initial block configuration. Falls back to `{}`. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration?: any;
}

export interface ColumnSetupBatch {
  versionBatchId: string;
  startedAt: number;
}

// ────────────────────────────────────────────
// Store
// ────────────────────────────────────────────

/**
 * Manages column configuration, sidebar UI state, and multi-column
 * selection for a **single sheet**.
 *
 * One instance of this class is created per sheet and owned by
 * `SmartTableStore`.  That means configs survive React re-renders and
 * are automatically cleaned up when the sheet is removed.
 *
 * Domain logic is split across three focused sub-stores:
 *
 *   • **`sidebar`** (`SidebarStore`) — which column is open in the
 *     config panel and whether the panel is visible.
 *   • **`selection`** (`ColumnSelectionStore`) — the set of columns
 *     the user has CMD+Clicked for batch operations.
 *   • **root** — column config CRUD and cross-cutting coordination
 *     methods that keep sidebar and selection mutually exclusive.
 */
export class ColumnConfigStore {
  // ── Sub-stores ────────────────────────────
  readonly sidebar = new SidebarStore();
  readonly selection = new ColumnSelectionStore();

  // ── Column config state ───────────────────
  /** Map of field → ColumnConfig for every configured column. */
  columnConfigs = new Map<string, ColumnConfig>();

  /**
   * Last known clean (persisted/hydrated) snapshot per column.
   * Drafts are considered dirty when they differ from this baseline.
   */
  cleanBaselines = new Map<string, ColumnDraftSnapshot>();

  /** Short-lived setup batches for newly added columns, keyed by column id. */
  columnSetupBatches = new Map<string, ColumnSetupBatch>();

  /**
   * The block type used when creating new column configs.
   * Change this to alter the default for all future configs without
   * touching every call site.
   */
  defaultBlockType: BlockType = BlockType.PROMPT_TEMPLATE;

  constructor() {
    makeAutoObservable(this, {
      // Sub-stores are plain objects tracked via their own makeAutoObservable
      sidebar: false,
      selection: false,
      columnConfigs: observable,
      defaultBlockType: observable,
      cleanBaselines: observable,
      columnSetupBatches: observable,
      // Cross-cutting coordination
      openSidebar: action,
      closeSidebar: action,
      toggleColumnSelection: action,
      setColumnSelection: action,
      // Config CRUD
      remapColumnField: action,
      ensureColumnConfig: action,
      setColumnConfig: action,
      setCleanBaseline: action,
      hydrateCleanColumnConfig: action,
      clearCleanBaseline: action,
      removeColumnConfig: action,
      updateColumnBlockType: action,
      resetAllConfigs: action,
      // Column setup batching
      startColumnSetupBatch: action,
      finishColumnSetupBatch: action,
      remapColumnSetupBatch: action,
      // Computed
      selectedConfig: computed,
      configuredColumnCount: computed,
    });
  }

  // ── Internal helpers ──────────────────────

  /**
   * Build a fresh `ColumnConfig` using store defaults.
   * Centralises the default-creation logic so it isn't duplicated.
   */
  private _buildDefault(field: string, options?: CreateColumnConfigOptions): ColumnConfig {
    return {
      field,
      name: options?.displayName || field,
      blockType: options?.blockType ?? this.defaultBlockType,
      configuration: options?.configuration ?? {},
    };
  }

  // ────────────────────────────────────────────────────────────────
  // Cross-cutting coordination
  //
  // Sidebar and multi-column selection are mutually exclusive modes.
  // The methods below ensure that entering one mode always exits the
  // other — no exceptions.
  // ────────────────────────────────────────────────────────────────

  /**
   * Open the sidebar for `field`.
   *
   * Clears multi-column selection (the two modes are exclusive).
   * Draft hydration from persisted column data is handled by sidebar logic.
   */
  openSidebar = (field: string, _displayName?: string): void => {
    const previousField = this.sidebar.selectedField;
    if (previousField && previousField !== field) {
      this.finishColumnSetupBatch(previousField);
    }

    this.selection.clear();
    this.sidebar.open(field);
  };

  /**
   * Close the sidebar panel.
   *
   * Delegates to `sidebar.close()`.  `sidebar.selectedField` is
   * intentionally preserved for the close animation — call
   * `sidebar.clearField()` after the transition ends.
   */
  closeSidebar = (): void => {
    if (this.sidebar.selectedField) {
      this.finishColumnSetupBatch(this.sidebar.selectedField);
    }
    this.sidebar.close();
  };

  /**
   * Toggle a column in the multi-select set (CMD+Click behaviour).
   *
   * Closes the sidebar if it was open — the two modes are exclusive.
   */
  toggleColumnSelection = (field: string): void => {
    if (this.sidebar.isOpen) {
      if (this.sidebar.selectedField) {
        this.finishColumnSetupBatch(this.sidebar.selectedField);
      }
      this.sidebar.reset();
    }
    this.selection.toggle(field);
  };

  /** Replace the multi-select set and close the sidebar. */
  setColumnSelection = (fields: readonly string[], lastToggled?: string | null): void => {
    if (this.sidebar.isOpen) {
      if (this.sidebar.selectedField) {
        this.finishColumnSetupBatch(this.sidebar.selectedField);
      }
      this.sidebar.reset();
    }
    this.selection.setSelection(fields, lastToggled);
  };

  // ────────────────────────────────────────────────────────────────
  // Config CRUD
  // ────────────────────────────────────────────────────────────────

  /**
   * Remap an existing column's field name to a new field name.
   * This is used when a temp column is successfully saved to the server.
   */
  remapColumnField = (oldField: string, newField: string): void => {
    const existing = this.columnConfigs.get(oldField);
    if (existing) {
      this.columnConfigs.delete(oldField);
      this.columnConfigs.set(newField, { ...existing, field: newField });
    }

    const existingBaseline = this.cleanBaselines.get(oldField);
    if (existingBaseline) {
      this.cleanBaselines.delete(oldField);
      this.cleanBaselines.set(newField, {
        ...existingBaseline,
        columnId: newField,
        configuration: cloneDeep(existingBaseline.configuration ?? {}),
      });
    }

    this.remapColumnSetupBatch(oldField, newField);

    if (this.sidebar.selectedField === oldField) {
      this.sidebar.selectedField = newField;
    }

    if (this.selection.has(oldField)) {
      this.selection.toggle(oldField);

      if (!this.selection.has(newField)) {
        this.selection.toggle(newField);
      }

      if (this.selection.lastToggled === oldField) {
        this.selection.lastToggled = newField;
      }
    }
  };

  /**
   * Return the config for `field`, creating it with defaults if it
   * doesn't exist yet.
   */
  ensureColumnConfig = (field: string, options?: CreateColumnConfigOptions): ColumnConfig => {
    const existing = this.columnConfigs.get(field);
    if (existing) {
      return existing;
    }
    const config = this._buildDefault(field, options);
    this.columnConfigs.set(field, config);
    return config;
  };

  /**
   * Merge `config` into the entry for `field`.
   *
   * - If the entry already exists, the provided keys are shallow-merged.
   * - If it does not exist, a new entry is created with store defaults
   *   for any omitted keys.
   *
   * **`config.field` is always ignored** — the map key (`field` argument)
   * is the single source of truth.
   */
  setColumnConfig = (field: string, config: Partial<Omit<ColumnConfig, "field">>): void => {
    const existing = this.columnConfigs.get(field);
    if (existing) {
      this.columnConfigs.set(field, {
        ...existing,
        ...config,
        field,
      });
    } else {
      this.columnConfigs.set(field, {
        field,
        name: config.name ?? field,
        blockType: config.blockType ?? this.defaultBlockType,
        configuration: config.configuration ?? {},
      });
    }
  };

  /**
   * Change the block type for a column, resetting its configuration.
   *
   * Auto-creates the config entry when it doesn't exist.
   */
  updateColumnBlockType = (field: string, blockType: BlockType): void => {
    const existing = this.columnConfigs.get(field);
    if (existing) {
      this.columnConfigs.set(field, {
        ...existing,
        blockType,
        configuration: {},
      });
    } else {
      this.columnConfigs.set(field, this._buildDefault(field, { blockType }));
    }
  };

  /**
   * Replace the clean baseline for a column.
   *
   * Call this whenever server/query hydration establishes authoritative state,
   * and after successful saves are reconciled.
   */
  setCleanBaseline = (field: string, snapshot: ColumnDraftSnapshot): void => {
    this.cleanBaselines.set(
      field,
      normalizeColumnDraftSnapshot({
        ...snapshot,
        columnId: field,
      }),
    );
  };

  /**
   * Atomically set the clean baseline and replace the local draft with that
   * clean state. Keeping both writes in one MobX action prevents reactions from
   * observing a transient dirty state during hydration/server refresh.
   */
  hydrateCleanColumnConfig = (field: string, snapshot: ColumnDraftSnapshot): void => {
    const cleanSnapshot = normalizeColumnDraftSnapshot({
      ...snapshot,
      columnId: field,
    });

    this.cleanBaselines.set(field, cleanSnapshot);
    this.columnConfigs.set(field, {
      field,
      name: cleanSnapshot.name,
      blockType: cleanSnapshot.blockType,
      configuration: cloneDeep(cleanSnapshot.configuration),
    });
  };

  /** Clear the clean baseline for a single column. */
  clearCleanBaseline = (field: string): void => {
    this.cleanBaselines.delete(field);
  };

  /**
   * Build a normalized draft snapshot for `field` from local config state.
   * Returns `null` when no local config exists.
   */
  getDraftSnapshot = (field: string): ColumnDraftSnapshot | null => {
    const config = this.columnConfigs.get(field);
    if (!config) {
      return null;
    }

    return {
      columnId: field,
      name: config.name,
      blockType: config.blockType,
      configuration: normalizeColumnConfiguration(
        config.blockType,
        (config.configuration ?? {}) as Record<string, unknown>,
      ),
    };
  };

  /** Read the clean baseline snapshot for `field`, if known. */
  getCleanBaseline = (field: string): ColumnDraftSnapshot | null => {
    const snapshot = this.cleanBaselines.get(field);
    if (!snapshot) {
      return null;
    }

    return normalizeColumnDraftSnapshot({
      ...snapshot,
      columnId: field,
    });
  };

  /** Whether `snapshot` differs from the clean baseline for `field`. */
  isDirty = (field: string, snapshot: ColumnDraftSnapshot | null): boolean => {
    const baseline = this.cleanBaselines.get(field);

    if (!snapshot) {
      return false;
    }

    if (!baseline) {
      return true;
    }

    return !isEqual(normalizeColumnDraftSnapshot(snapshot), normalizeColumnDraftSnapshot(baseline));
  };

  /** Remove the config entry for a single column. */
  removeColumnConfig = (field: string): void => {
    this.columnConfigs.delete(field);
    this.cleanBaselines.delete(field);
    this.finishColumnSetupBatch(field);
  };

  /** Full reset — clear all configs, sidebar, selection, and transient batches. */
  resetAllConfigs = (): void => {
    this.columnConfigs.clear();
    this.cleanBaselines.clear();
    this.columnSetupBatches.clear();
    this.sidebar.reset();
    this.selection.clear();
  };

  startColumnSetupBatch = (columnId: string, versionBatchId: string): void => {
    this.columnSetupBatches.set(columnId, {
      versionBatchId,
      startedAt: Date.now(),
    });
  };

  getColumnSetupBatch = (columnId: string): ColumnSetupBatch | null =>
    this.columnSetupBatches.get(columnId) ?? null;

  finishColumnSetupBatch = (columnId: string): void => {
    this.columnSetupBatches.delete(columnId);
  };

  remapColumnSetupBatch = (oldColumnId: string, newColumnId: string): void => {
    const batch = this.columnSetupBatches.get(oldColumnId);
    if (!batch) {
      return;
    }

    this.columnSetupBatches.delete(oldColumnId);
    this.columnSetupBatches.set(newColumnId, batch);
  };

  // ────────────────────────────────────────────────────────────────
  // Computed
  // ────────────────────────────────────────────────────────────────

  /**
   * The config entry for the column currently open in the sidebar,
   * or `null` if no column is selected / the config doesn't exist.
   */
  get selectedConfig(): ColumnConfig | null {
    const field = this.sidebar.selectedField;
    if (!field) {
      return null;
    }
    return this.columnConfigs.get(field) ?? null;
  }

  get configuredColumnCount(): number {
    return this.columnConfigs.size;
  }
}
